/* Aydins: retain deliberate choices across Kalles section-render responses.
 * Shopify's resolved variant supplies availability/media, never implied choices.
 * Keep the vendor's variant, price, inventory and engraving handlers intact.
 */
(() => {
  const states = new WeakMap();
  const isSize = field => /^(ring\s+)?size$/i.test(field.dataset.ayOptionName || "");
  const fields = picker => [...picker.querySelectorAll("fieldset[data-index]")];
  const controls = field => [...field.querySelectorAll("input[data-value-id], button[data-value-id]")];
  const selected = field => controls(field).find(el => el.checked || el.getAttribute("aria-selected") === "true")?.dataset.valueId;
  const sticky = picker => document.querySelector(`hdt-sticky-btn-atc[form="${CSS.escape(picker.getAttribute("form"))}"]`);

  function restore(picker, choices) {
    const values = [];
    fields(picker).forEach(field => {
      let value = "";
      controls(field).forEach(el => {
        const chosen = el.dataset.valueId === choices[field.dataset.index];
        if (el.tagName === "INPUT") el.checked = chosen;
        else el.setAttribute("aria-selected", String(chosen));
        if (chosen) value = el.value;
      });
      const hidden = field.querySelector('input[is-value][type="hidden"]');
      if (hidden) hidden.value = value;
      const label = value || picker.dataset.ayChooseOption;
      field.querySelectorAll("[update-value], button.hdt-select").forEach(el => { el.textContent = label; });
      const list = field.querySelector("hdt-richlist");
      if (list) list.setAttribute("selected", value);
      if (value) values.push(value);
    });
    picker.options = values;
  }

  function lock(picker) {
    picker.setAttribute("no-pick", "");
    picker.oldVariant = null; // Re-selecting the same variant must refresh both forms.
    const form = document.getElementById(picker.getAttribute("form"));
    const bar = sticky(picker);
    if (bar) bar.style.setProperty("display", "none", "important");
    form?.querySelectorAll(".shopify-payment-button").forEach(el => { el.hidden = true; });
    [form, bar?.querySelector("form")].filter(Boolean).forEach(form => {
      const id = form.querySelector('[name="id"]');
      if (id) { id.value = ""; id.disabled = true; }
      const button = form.querySelector('[name="add"]');
      if (button) {
        button.disabled = true;
        const label = button.querySelector(".hdt-btn-atc_text") || button.firstElementChild;
        if (label) label.textContent = picker.dataset.ayChooseOptions;
        button.querySelectorAll("hdt-qty-price").forEach(el => { el.hidden = true; });
      }
    });
    if (picker.hasAttribute("update-url")) {
      const url = new URL(location.href);
      if (url.searchParams.has("variant") || url.searchParams.has("option_values")) {
        url.searchParams.delete("variant");
        url.searchParams.delete("option_values");
        history.replaceState(history.state, "", url);
      }
    }
  }

  // Shopify rates each option value against the resolved variant, which includes sizes the customer never
  // chose, so 8MM read "sold out" only because the hidden default size (4) is not made in 8MM. Re-rate every
  // value against the customer's explicit choices and real per-variant availability: available if any
  // matching variant is buyable, "sold out" if matching variants exist but none is, "not made" if none exist.
  const variantLists = {};
  const variantsFor = handle => variantLists[handle] ||= fetch(`${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(handle)}.js`)
    .then(r => (r.ok ? r.json() : null)).then(p=>{const variants=p?.variants;if(variants?.length>=250&&/^(round|oval|princess|cushion|elongated-cushion|emerald|marquise|pear|radiant|asscher|heart|old-mine)-lab-grown-diamond-engagement-ring-(10k|14k)-two-tone-gold$/.test(handle))return null;return variants||null}).catch(() => null);

  async function refreshAvailability(picker) {
    const variants = await variantsFor(picker.getAttribute("handle"));
    if (!variants || !picker.isConnected) return;
    const all = fields(picker);
    const chosen = all.map(field => controls(field).find(el => el.checked || el.getAttribute("aria-selected") === "true")?.value ?? null);
    all.forEach((field, i) => {
      const at = Number(field.dataset.index);
      const others = all.map((f, j) => (j !== i && chosen[j] != null ? [Number(f.dataset.index), chosen[j], isSize(f)] : null)).filter(Boolean);
      controls(field).forEach(el => {
        const matches = variants.filter(v => v.options[at] === el.value && others.every(([k, value]) => v.options[k] === value));
        const label = el.tagName === "INPUT" ? field.querySelector(`label[for="${CSS.escape(el.id)}"]`) : el;
        let note = label?.querySelector(":scope > .sr-only");
        if (matches.some(v => v.available)) {
          el.classList.remove("is-disabled");
          note?.remove();
          return;
        }
        el.classList.add("is-disabled");
        if (!label) return;
        if (!note) { note = document.createElement("span"); note.className = "sr-only"; label.prepend(note); }
        note.textContent = matches.length ? "Variant sold out" : `Not made in ${others.map(([, value, size]) => (size ? `size ${value}` : value)).join(" / ")}`;
      });
    });
  }

  function showWidthImage(picker, variant) {
    if (!variant?.mdid) return;
    const media = picker.productMedia;
    const target = media?.querySelector(`.hdt-product-media__main [data-media-id="${CSS.escape(String(variant.mdid))}"]`);
    if (target && media.sliderMain?.isActive) media.sliderMain.goToTarget(target);
  }

  customElements.whenDefined("hdt-variant-picker").then(() => {
    const proto = customElements.get("hdt-variant-picker").prototype;
    const originalUpdate = proto.updateOptions;
    const originalSelect = proto.selectVariant;
    if (proto.ayExplicitOptionsInstalled) return;
    proto.ayExplicitOptionsInstalled = true;

    proto.updateOptions = function () {
      if (!this.hasAttribute("data-ay-explicit-options")) return originalUpdate.apply(this, arguments);
      let state = states.get(this);
      if (!state) {
        state = { revision: 0, queue: Promise.resolve(), choices: {} };
        states.set(this, state);
      }
      const choices = Object.fromEntries(fields(this).map(field => [field.dataset.index, selected(field)]));
      const changed = fields(this).find(field => Number(field.dataset.index) === this.clickedCurrentIndex);
      const widthChanged = changed?.dataset.ayOptionName === "width";
      if (widthChanged) {
        fields(this).filter(isSize)
          .forEach(field => { delete choices[field.dataset.index]; });
      }
      state.choices = choices;
      const revision = ++state.revision;
      state.ready = false;
      restore(this, choices);
      lock(this);

      // Serialize native renders; older responses cannot enable an obsolete variant.
      const run = state.queue.then(async () => {
        if (revision !== state.revision) { this.options = []; return; }
        restore(this, choices);
        const before = this.firstElementChild;
        await originalUpdate.call(this);
        restore(this, state.choices);
        if (revision !== state.revision) {
          this.options = [];
          lock(this);
          return;
        }
        const variant = this.currentVariant;
        const complete = this.options.length === Number(this.dataset.size);
        const matches = complete && variant?.options.every((value, index) => value === this.options[index]);
        state.ready = before !== this.firstElementChild && matches;
        if (!state.ready) {
          // A nonexistent combination must never fall back to a different size.
          if (complete) {
            fields(this).filter(isSize)
              .forEach(field => { delete state.choices[field.dataset.index]; });
            restore(this, state.choices);
          }
          lock(this);
          if (widthChanged && before !== this.firstElementChild) showWidthImage(this, variant);
          if (this.options.length === Number(this.dataset.size)) this.options = [];
        }
        this.classList.remove("loading"); // Vendor's incomplete branch returns before doing this.
        await refreshAvailability(this);
      }).catch(error => {
        state.ready = false;
        restore(this, state.choices);
        this.options = [];
        lock(this);
        this.classList.remove("loading");
        console.error("Aydins option selection could not be completed", error);
      });
      state.queue = run;
      return run;
    };

    proto.selectVariant = function () {
      if (!this.hasAttribute("data-ay-explicit-options")) return originalSelect.apply(this, arguments);
      const state = states.get(this);
      if (state && (!state.ready || !this.currentVariant?.options.every((value, index) => value === this.options[index]))) {
        lock(this);
        return;
      }
      const bar = sticky(this);
      if (bar) bar.style.removeProperty("display");
      this.masterInput?.form?.querySelectorAll(".shopify-payment-button").forEach(el => { el.hidden = false; });
      if (this.masterInput) this.masterInput.disabled = false;
      const stickyId = bar?.querySelector('[name="id"]');
      if (stickyId) { stickyId.disabled = false; stickyId.value = this.currentVariant.id; }
      const result = originalSelect.apply(this, arguments);
      // StickyATC skips its update when reselecting the same ID; restore its button too.
      const button = bar?.querySelector('[name="add"]');
      if (button) {
        button.disabled = !this.currentVariant.available;
        const label = button.querySelector(".hdt-btn-atc_text") || button.firstElementChild;
        if (label) label.textContent =
          window.themeHDN.extras[this.currentVariant.variant_state.pre_order ? "preOrder" :
            this.currentVariant.available ? "addToCart" : "soldOut"];
      }
      return result;
    };

    document.querySelectorAll("hdt-variant-picker[data-ay-explicit-options]").forEach(picker => { refreshAvailability(picker); });

    // Capture also covers newly inserted quick-view pickers and keyboard submission.
    document.addEventListener("submit", event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const bar = form.closest("hdt-sticky-btn-atc");
      const id = bar?.getAttribute("form") || form.getAttribute("id");
      const picker = document.querySelector(`hdt-variant-picker[data-ay-explicit-options][form="${CSS.escape(id)}"]`);
      if (picker?.hasAttribute("no-pick")) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  });
})();
